/** SD-4692 — décisions PURES du voyant d'état du service et de la carte d'état de la demande (clés de messages, jamais de texte), testées par `node:test`. */

import type { RunState } from '@ggvie-consulting/lib/runState'
import type { MessageParams, ServiceMessageKey } from '@ggvie-consulting/service/messages'

export type ServiceLevel = 'vert' | 'orange' | 'rouge' | 'gris'
export type TurnClass = 'conseil' | 'collecte' | 'kit' | 'rapide'

export interface MessageRef {
  key: ServiceMessageKey
  params?: MessageParams
}

export interface EtaRange {
  min_s: number
  max_s: number | null
  label: string | null
}

export type ServiceStart = 'immediate' | 'wait' | 'suspended'

export interface ServiceLoad {
  level: ServiceLevel
  since: string | null
  source: string | null
  /** Codes de la cause servie (attente, suspension, lenteur, retour à la normale) : seule source des raisons. */
  reasons: string[]
  /** Démarrage d'une nouvelle demande, du même instantané que le niveau ; `null` = inconnu (gris, ancien serveur). */
  start: ServiceStart | null
  eta_new: EtaRange | null
  /** Pourquoi aucune fourchette n'est servie en attente (`no_release`). */
  eta_new_reason: string | null
  slow_after_s: Partial<Record<TurnClass, number>>
}

/** Cause unique dont dérivent libellé, détail et annonce du voyant. */
export interface ServiceCause {
  start: ServiceStart
  suspension: 'maintenance' | 'paused' | null
  eta: MessageRef | null
  wait: 'waiting' | 'worker'
  speed: 'degraded' | 'slow' | null
  recovering: boolean
}

export interface ServiceSnapshot {
  maintenance: 'off' | 'scheduled' | 'active'
  server_time: string | null
  load: ServiceLoad | null
}

/** Échecs consécutifs de lecture au-delà desquels le voyant passe au gris. */
export const SERVICE_FAILURES_BEFORE_GREY = 2
export const SERVICE_POLL_VISIBLE_MS = 10_000
export const SERVICE_POLL_HIDDEN_MS = 30_000
/** Une demande admise sous ce délai démarre sans afficher de carte « en file ». */
export const QUEUED_CARD_DELAY_MS = 1_500
/** Durée d'affichage des lignes transitoires (suivi repris, réponse rechargée). */
export const TRANSIENT_CARD_MS = 5_000

/** Seuils « plus long que d'habitude » de repli : défauts des réglages serveur, remplacés par les valeurs servies. */
export const DEFAULT_SLOW_AFTER_S: Readonly<Record<TurnClass, number>> = { conseil: 35, collecte: 20, kit: 25, rapide: 15 }
/** Une bascule « attente ↔ démarrage immédiat » à niveau égal n'est annoncée qu'une fois par minute au plus. */
export const ANNOUNCE_MIN_INTERVAL_MS = 60_000

const LEVELS: readonly ServiceLevel[] = ['vert', 'orange', 'rouge', 'gris']
const ETA_LABELS = ['quelques_secondes', 'moins_d_une_minute', '1_a_3_min', '3_a_10_min', 'plus_de_10_min'] as const
type EtaLabel = (typeof ETA_LABELS)[number]
const ETA_BOUNDS: ReadonlyArray<readonly [number, EtaLabel]> = [
  [20, 'quelques_secondes'],
  [60, 'moins_d_une_minute'],
  [180, '1_a_3_min'],
  [600, '3_a_10_min'],
]
const WAITING_REASONS: ReadonlySet<string> = new Set(['waiting', 'wait', 'llm_saturated', 'vllm_waiting', 'cache'])
const WORKER_REASONS: ReadonlySet<string> = new Set(['worker_saturated', 'worker'])
const DEGRADED_REASONS: ReadonlySet<string> = new Set(['degraded', 'degradation'])
// Démarrage suspendu : aucune fourchette n'a de sens tant que le service n'a pas repris.
const SUSPENDED_REASONS: ReadonlySet<string> = new Set(['paused', 'maintenance'])
const STARTS: readonly ServiceStart[] = ['immediate', 'wait', 'suspended']
// Lignes du détail qui décrivent le démarrage ou rassurent : jamais relues par l'annonce.
const UNANNOUNCED_DETAILS: ReadonlySet<ServiceMessageKey> = new Set([
  'detail_start_now',
  'detail_start_eta',
  'detail_start_when_free',
  'detail_short_wait',
  'detail_queue_keep',
  'detail_unknown',
])

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null
}

export function normalizeLevel(raw: unknown): ServiceLevel {
  return typeof raw === 'string' && (LEVELS as readonly string[]).includes(raw) ? (raw as ServiceLevel) : 'gris'
}

export function readEtaRange(raw: unknown): EtaRange | null {
  if (!raw || typeof raw !== 'object') return null
  const eta = raw as { min_s?: unknown; max_s?: unknown; label?: unknown }
  const min = asNumber(eta.min_s)
  const label = asString(eta.label)
  if (min === null && label === null) return null
  return { min_s: min ?? 0, max_s: asNumber(eta.max_s), label }
}

// Raisons du MÊME instantané que le niveau : aucune seconde source (refus d'admission, champ de maintenance).
function readReasons(load: Record<string, unknown>): string[] {
  const reasons = new Set<string>()
  if (Array.isArray(load.reasons)) {
    for (const item of load.reasons) {
      const code = typeof item === 'string' ? item : asString((item as { code?: unknown } | null)?.code)
      if (code) reasons.add(code)
    }
  }
  return [...reasons]
}

function readStart(raw: unknown): ServiceStart | null {
  return typeof raw === 'string' && (STARTS as readonly string[]).includes(raw) ? (raw as ServiceStart) : null
}

/** Lecture tolérante du corps de `GET /service/status` ; `null` si le corps n'est pas un statut. */
export function parseServiceStatus(body: unknown): ServiceSnapshot | null {
  if (!body || typeof body !== 'object') return null
  const raw = body as { maintenance?: unknown; server_time?: unknown; load?: unknown }
  if (raw.load === undefined) return null
  const maintenance = raw.maintenance === 'scheduled' || raw.maintenance === 'active' ? raw.maintenance : 'off'
  let load: ServiceLoad | null = null
  if (raw.load && typeof raw.load === 'object') {
    const l = raw.load as Record<string, unknown>
    const slowAfter: Partial<Record<TurnClass, number>> = {}
    if (l.request_durations && typeof l.request_durations === 'object') {
      const durations = l.request_durations as Record<string, { slow_after_s?: unknown } | null | undefined>
      for (const klass of Object.keys(DEFAULT_SLOW_AFTER_S) as TurnClass[]) {
        const value = asNumber(durations[klass]?.slow_after_s)
        if (value !== null && value > 0) slowAfter[klass] = value
      }
    }
    load = {
      level: normalizeLevel(l.level),
      since: asString(l.since),
      source: asString(l.source),
      reasons: readReasons(l),
      start: readStart(l.start),
      eta_new: readEtaRange(l.eta_new),
      eta_new_reason: asString(l.eta_new_reason),
      slow_after_s: slowAfter,
    }
  }
  return { maintenance, server_time: asString(raw.server_time), load }
}

/** Niveau affiché : gris dès que l'état est illisible (aucune lecture, ou deux échecs consécutifs). */
export function displayedLevel(load: ServiceLoad | null, consecutiveFailures: number): ServiceLevel {
  if (!load || consecutiveFailures >= SERVICE_FAILURES_BEFORE_GREY) return 'gris'
  return load.level
}


/** Fourchette (« 1 à 3 minutes ») : clé servie d'abord, sinon bande du point haut ; sans borne haute, plus de 10 min. */
export function formatEtaRange(eta: EtaRange | null | undefined): MessageRef | null {
  if (!eta) return null
  if (eta.label && (ETA_LABELS as readonly string[]).includes(eta.label)) return { key: `eta_${eta.label as EtaLabel}` }
  if (eta.max_s === null) return { key: 'eta_plus_de_10_min' }
  for (const [limit, band] of ETA_BOUNDS) if (eta.max_s <= limit) return { key: `eta_${band}` }
  return { key: 'eta_plus_de_10_min' }
}

/** Cause servie par le serveur ; sans `start` (ancien serveur), déduite du même corps de réponse, jamais d'ailleurs. */
export function serviceCause(load: ServiceLoad): ServiceCause {
  const reasons = load.reasons
  const suspension = reasons.includes('maintenance') ? 'maintenance' : reasons.includes('paused') ? 'paused' : null
  const waits = load.eta_new !== null || reasons.some((r) => WAITING_REASONS.has(r) || WORKER_REASONS.has(r))
  let start: ServiceStart
  if (load.start === 'suspended' || suspension !== null) start = 'suspended'
  else if (load.start !== null) start = load.start
  else start = waits ? 'wait' : 'immediate'
  return {
    start,
    suspension: start === 'suspended' ? (suspension ?? 'paused') : null,
    eta: start === 'wait' ? formatEtaRange(load.eta_new) : null,
    wait: reasons.some((r) => WORKER_REASONS.has(r)) ? 'worker' : 'waiting',
    speed: reasons.some((r) => DEGRADED_REASONS.has(r)) ? 'degraded' : reasons.includes('slow') ? 'slow' : null,
    recovering: reasons.includes('recovering'),
  }
}

// Démarrage suspendu (gel, maintenance) : la cause prime sur le niveau dans le libellé et l'annonce.
function suspended(load: ServiceLoad | null, level: ServiceLevel): boolean {
  return load !== null && level !== 'gris' && serviceCause(load).start === 'suspended'
}

/** Libellé du voyant, dérivé de la même cause que le détail : démarrage suspendu, sinon le niveau de charge. */
export function levelLabel(load: ServiceLoad | null, level: ServiceLevel): MessageRef {
  return { key: suspended(load, level) ? 'level_suspended' : `level_${level}` }
}

/** Lignes du détail du voyant, toutes dérivées de la cause : démarrage, raison (attente courte au vert), vitesse, puis réassurance. */
export function levelDetails(load: ServiceLoad | null, level: ServiceLevel): MessageRef[] {
  if (!load || level === 'gris') return [{ key: 'detail_unknown' }]
  const cause = serviceCause(load)
  const speed: MessageRef[] = cause.speed ? [{ key: cause.speed === 'degraded' ? 'detail_degraded' : 'detail_slow' }] : []
  if (cause.start === 'suspended') {
    const head: MessageRef = { key: cause.suspension === 'maintenance' ? 'detail_maintenance' : 'detail_paused' }
    return [head, ...speed, { key: 'detail_queue_keep' }]
  }
  if (cause.start === 'wait') {
    const head: MessageRef = cause.eta
      ? { key: 'detail_start_eta', params: { eta: cause.eta } }
      : { key: 'detail_start_when_free' }
    // Au vert, « l'attente reste courte » n'est promis qu'avec une estimation.
    const why: MessageRef[] =
      level !== 'vert' ? [{ key: cause.wait === 'worker' ? 'detail_worker' : 'detail_waiting' }] : cause.eta ? [{ key: 'detail_short_wait' }] : []
    return [head, ...why, ...speed, { key: 'detail_queue_keep' }]
  }
  const lines: MessageRef[] = [{ key: 'detail_start_now' }, ...speed]
  if (cause.recovering) lines.push({ key: 'detail_recovering' })
  return lines
}

/** Annonce du lecteur d'écran : phrase de l'état (suspension, sinon niveau), puis les seules lignes de CAUSE du détail (jamais de contradiction). */
export function levelAnnouncement(load: ServiceLoad | null, level: ServiceLevel): MessageRef[] {
  const head: MessageRef = { key: suspended(load, level) ? 'announce_suspended' : `announce_${level}` }
  if (level === 'gris') return [head]
  return [head, ...levelDetails(load, level).filter((line) => !UNANNOUNCED_DETAILS.has(line.key))]
}

/** Clé d'annonce : niveau et catégorie de démarrage (au vert, seule la suspension compte) ; raison, vitesse et fourchette n'y entrent pas. */
export function announcementKey(load: ServiceLoad | null, level: ServiceLevel): string {
  if (level === 'gris' || !load) return 'gris'
  const start = serviceCause(load).start
  if (level === 'vert' && start !== 'suspended') return 'vert'
  return `${level}|${start}`
}

/** Faut-il annoncer ? Changement de niveau, entrée ou sortie de suspension ; attente ↔ immédiat au plus une fois par minute. */
export function shouldAnnounce(lastAnnouncedKey: string, nextKey: string, msSinceLastAnnouncement: number): boolean {
  if (nextKey === lastAnnouncedKey) return false
  const [lastLevel, lastStart = ''] = lastAnnouncedKey.split('|')
  const [nextLevel, nextStart = ''] = nextKey.split('|')
  if (lastLevel !== nextLevel) return true
  if (lastStart === 'suspended' || nextStart === 'suspended') return true
  return msSinceLastAnnouncement > ANNOUNCE_MIN_INTERVAL_MS
}

/** Dernière annonce faite (ou état initial mémorisé sans annonce). */
export interface Announced {
  key: string
  at: number
}

/** Un relevé du voyant : lignes à annoncer (ou `null`) et mémoire mise à jour ; l'état initial n'est jamais annoncé. */
export function announce(
  last: Announced | null,
  load: ServiceLoad | null,
  level: ServiceLevel,
  nowMs: number,
): { last: Announced; lines: MessageRef[] | null } {
  const key = announcementKey(load, level)
  if (last === null) return { last: { key, at: nowMs }, lines: null }
  if (!shouldAnnounce(last.key, key, nowMs - last.at)) return { last, lines: null }
  return { last: { key, at: nowMs }, lines: levelAnnouncement(load, level) }
}

/** Décision 8 / constat #22 : le blocage d'envoi ne dépend QUE de l'état de la demande, jamais du flux réseau. */
export function inputBlocked(run: Pick<RunState, 'blocks_input'>): boolean {
  return run.blocks_input
}

/** Carte de relance : de la relance au début de la réponse de la tentative relancée (attente, file, admise, en cours). */
export function retryCardVisible(run: Pick<RunState, 'retried' | 'answer_started' | 'state'>): boolean {
  return run.retried && !run.answer_started && (run.state === 'queued' || run.state === 'admitted' || run.state === 'running')
}

/** Carte « en file » : aussitôt si une attente réelle est signalée (relance, raison, verdict, rang > 0), sinon après 1,5 s en file. */
export function shouldShowQueuedCard(
  run: Pick<RunState, 'state' | 'position' | 'retried' | 'reason' | 'eta_reason'>,
  queuedForMs: number,
): boolean {
  if (run.state !== 'queued') return false
  const waits = run.retried || run.reason !== null || run.eta_reason !== null || (run.position ?? 0) > 0
  return waits || queuedForMs >= QUEUED_CARD_DELAY_MS
}

/** Annuler : en file et en cours, jamais une fois la réponse délivrée ni pendant l'enregistrement. */
export function canCancelRun(
  run: Pick<RunState, 'state' | 'phase' | 'answer_streamed' | 'blocks_input' | 'cancel' | 'run_id'>,
): boolean {
  if (!run.blocks_input || !run.run_id || run.cancel === 'requested') return false
  if (run.answer_streamed || run.phase === 'committing') return false
  return run.state === 'queued' || run.state === 'admitted' || run.state === 'running'
}

export function slowAfterSeconds(turnClass: TurnClass, load: ServiceLoad | null): number {
  return load?.slow_after_s[turnClass] ?? DEFAULT_SLOW_AFTER_S[turnClass]
}

/** Classe de la demande pour le seuil de lenteur : kit en cours, réponse à une collecte, sinon conseil. */
export function turnClassFor(run: Pick<RunState, 'kind'>, kitRunning: boolean): TurnClass {
  if (kitRunning) return 'kit'
  if (run.kind === 'choices') return 'collecte'
  return 'conseil'
}

export type RequestCardKind =
  | 'queued'
  | 'retrying'
  | 'running_slow'
  | 'resumed_running'
  | 'journal_gap'
  | 'presumed_lost'
  | 'finalizing'
  | 'cancelling'
  | 'cancel_refused'
  | 'connection_lost'
  | 'failed_technical'
  | 'failed_exhausted'
  | 'failed_business'
  | 'failed_past'
  | 'cancelled'
  | 'cancelled_past'
  | 'state_unknown'
  | 'rejected'
  | 'stream_error'

export type RequestCardTone = 'info' | 'warning' | 'error'

export interface RequestCardView {
  kind: RequestCardKind
  tone: RequestCardTone
  lines: MessageRef[]
  /** Texte serveur brut (event SSE `error`), affiché tel quel. */
  rawLine: string | null
  showCancel: boolean
  cancelPending: boolean
  showResend: boolean
  dismissible: boolean
}

export interface RequestCardInput {
  run: RunState
  /** Durée passée dans l'état courant (ms), mesurée par l'appelant. */
  stateElapsedMs: number
  slowAfterS: number
  /** Le panneau « Erreurs API » affiche déjà l'erreur métier : la carte s'efface. */
  apiErrorShown: boolean
  /** Rattachement réussi après une coupure, depuis moins de quelques secondes. */
  resumedRecently: boolean
  /** Rejeu complet du journal (`run.journal_gap`), depuis moins de quelques secondes. */
  gapRecently: boolean
}

/** Clé du texte d'un refus de la demande (409/503, réseau, session, maintenance). */
export function rejectionKey(code: string): ServiceMessageKey {
  if (code === 'run_active') return 'rejected_run_active'
  if (code === 'run_id_conflict') return 'rejected_conflict'
  if (code === 'agent_mismatch') return 'rejected_agent_mismatch'
  if (code === 'run_state_unavailable' || code === 'checkpointer_unavailable' || code === 'dispatch_failed') {
    return 'rejected_unavailable'
  }
  if (code === 'network') return 'rejected_network'
  if (code === 'session_expired') return 'rejected_session'
  if (code === 'maintenance') return 'rejected_maintenance'
  return 'rejected_http'
}

function card(
  kind: RequestCardKind,
  tone: RequestCardTone,
  lines: MessageRef[],
  run: RunState,
  extra: Partial<Pick<RequestCardView, 'showResend' | 'dismissible' | 'rawLine'>> = {},
): RequestCardView {
  return {
    kind,
    tone,
    lines,
    rawLine: extra.rawLine ?? null,
    showCancel: canCancelRun(run),
    cancelPending: run.cancel === 'requested',
    showResend: extra.showResend ?? false,
    dismissible: extra.dismissible ?? false,
  }
}

function queueLines(run: RunState): MessageRef[] {
  const lines: MessageRef[] = []
  if (run.position !== null) {
    if (run.position <= 0) lines.push({ key: 'queued_next' })
    else if (run.position === 1) lines.push({ key: 'queued_position_one' })
    else lines.push({ key: 'queued_position_many', params: { count: run.position } })
  }
  if ((run.reason && SUSPENDED_REASONS.has(run.reason)) || run.eta_reason === 'suspended') lines.push({ key: 'queued_resume' })
  else {
    const eta = formatEtaRange(run.eta)
    if (eta) lines.push({ key: 'queued_eta', params: { eta } })
    else if (run.eta_reason === 'no_release') lines.push({ key: 'queued_when_free' })
  }
  if (run.reason === 'maintenance') lines.push({ key: 'queued_maintenance' })
  else if (run.reason && WORKER_REASONS.has(run.reason)) lines.push({ key: 'queued_worker' })
  else if (run.reason === 'paused') lines.push({ key: 'queued_paused' })
  else if (run.reason && WAITING_REASONS.has(run.reason)) lines.push({ key: 'queued_waiting' })
  lines.push({ key: 'queued_keep' })
  return lines
}

// Pendant l'attente avant relance, ni position ni estimation : seulement la reprise si le service est suspendu.
function retryLines(run: RunState): MessageRef[] {
  const head: MessageRef = { key: 'retrying' }
  if (run.retry_wait) {
    const resume = run.reason !== null && SUSPENDED_REASONS.has(run.reason)
    return resume ? [head, { key: 'queued_resume' }, { key: 'queued_keep' }] : [head, { key: 'queued_keep' }]
  }
  if (run.state === 'queued') return [head, ...queueLines(run)]
  return [head]
}

function canResend(run: RunState): boolean {
  return run.kind === 'message' && run.request_text.trim().length > 0
}

function cancelRefusalKey(code: string | null): ServiceMessageKey | null {
  if (!code || code === 'already_terminal') return null
  return code === 'too_late' ? 'cancel_too_late' : 'cancel_failed'
}

function failedCard(run: RunState, apiErrorShown: boolean): RequestCardView | null {
  const outcome = run.outcome
  const rawLine = run.stream_error
  if (outcome?.code === 'failed_business') {
    if (apiErrorShown) return null
    const lines: MessageRef[] = [{ key: 'failed_business' }]
    const fields = outcome.fields_to_fix ?? []
    if (fields.length > 0) lines.push({ key: 'failed_business_fields', params: { fields: fields.join(', ') } })
    return card('failed_business', 'error', lines, run, { rawLine, dismissible: true })
  }
  const extra = { rawLine, dismissible: true, showResend: canResend(run) }
  if (outcome?.detail?.startsWith('exhausted')) return card('failed_exhausted', 'error', [{ key: 'failed_exhausted' }], run, extra)
  if (run.restore_text !== null) return card('failed_technical', 'error', [{ key: 'failed_technical' }], run, extra)
  return card('failed_past', 'error', [{ key: 'failed_past' }], run, extra)
}

/** Unique carte d'état de la demande dans le fil ; `null` = rien à montrer (démarrage immédiat, réponse normale). */
export function requestCardView(input: RequestCardInput): RequestCardView | null {
  const { run } = input
  if (run.rejection && !(run.rejection.code === 'run_active' && run.blocks_input)) {
    return card('rejected', 'error', [{ key: rejectionKey(run.rejection.code) }], run, { dismissible: true })
  }
  if (run.state === 'unknown') return card('state_unknown', 'warning', [{ key: 'state_unknown' }], run, { dismissible: true })
  if (run.blocks_input) {
    if (run.cancel === 'requested') return card('cancelling', 'info', [{ key: 'cancelling' }], run)
    if (run.connection === 'lost') return card('connection_lost', 'warning', [{ key: 'connection_lost' }], run)
    // La carte ne promet une relance que si le serveur en annonce une.
    if ((run.effective_state === 'lost' || run.effective_state === 'dispatch_lost') && run.will_retry) {
      return card('presumed_lost', 'warning', [{ key: 'presumed_lost' }], run)
    }
    if (run.rejection?.code === 'run_active') return card('rejected', 'warning', [{ key: 'rejected_run_active' }], run)
    const refusal = cancelRefusalKey(run.cancel_refusal)
    if (retryCardVisible(run)) {
      if (refusal) return card('cancel_refused', 'warning', [{ key: refusal }], run)
      return card('retrying', 'warning', retryLines(run), run)
    }
    if (run.state === 'queued') {
      if (!shouldShowQueuedCard(run, input.stateElapsedMs)) return null
      return card('queued', 'info', [{ key: 'queued' }, ...queueLines(run)], run)
    }
    if (run.state === 'finalizing' || run.answer_streamed) return card('finalizing', 'info', [{ key: 'finalizing' }], run)
    if (refusal) return card('cancel_refused', 'warning', [{ key: refusal }], run)
    if (input.gapRecently) return card('journal_gap', 'info', [{ key: 'journal_gap' }], run)
    if (input.stateElapsedMs >= input.slowAfterS * 1000) return card('running_slow', 'info', [{ key: 'running_slow' }], run)
    if (input.resumedRecently) return card('resumed_running', 'info', [{ key: 'resumed_running' }], run)
    return null
  }
  if (run.state === 'failed') return failedCard(run, input.apiErrorShown)
  if (run.state === 'cancelled') {
    const live = run.restore_text !== null
    return card(live ? 'cancelled' : 'cancelled_past', 'warning', [{ key: live ? 'cancelled' : 'cancelled_past' }], run, {
      dismissible: true,
    })
  }
  if (run.stream_error) return card('stream_error', 'error', [], run, { rawLine: run.stream_error, dismissible: true })
  return null
}
